import { createBrowserRouter, Navigate } from 'react-router-dom';
import { OrderStatus } from '@lombok/shared';
import { ROLES_BY_GROUP } from '@/auth/roles';
import { isAppPath } from '@/lib/shellPrefs';
import { AppShell } from '@/components/AppShell';
import { Placeholder } from '@/components/Placeholder';
import { RoleGuard } from '@/routes/RoleGuard';
import { RootRedirect } from '@/routes/RootRedirect';
import { NotFoundPage } from '@/routes/NotFoundPage';

import { LandingPage } from '@/features/public-site/LandingPage';
import { LoginPage } from '@/features/auth/LoginPage';
import { RequestAccountPage } from '@/features/auth/RequestAccountPage';
import { AdminDashboard } from '@/features/admin/AdminDashboard';
import { ProductsListPage } from '@/features/products/ProductsListPage';
import { ProductCreatePage } from '@/features/products/ProductCreatePage';
import { ProductDetailPage } from '@/features/products/ProductDetailPage';
import { CustomersListPage } from '@/features/customer/CustomersListPage';
import { CustomerCreatePage } from '@/features/customer/CustomerCreatePage';
import { CustomerDetailPage } from '@/features/customer/CustomerDetailPage';
import { DistributorsListPage } from '@/features/distributors/DistributorsListPage';
import { DistributorCreatePage } from '@/features/distributors/DistributorCreatePage';
import { DistributorDetailPage } from '@/features/distributors/DistributorDetailPage';
import { DistributorAccountPage } from '@/features/distributors/DistributorAccountPage';
import { DistributorOrderCreatePage } from '@/features/orders/DistributorOrderCreatePage';
import { SubaccountDetailPage } from '@/features/distributors/SubaccountDetailPage';
import { InventoryListPage } from '@/features/inventory/InventoryListPage';
import { OrderCreatePage } from '@/features/orders/OrderCreatePage';
import { OrdersListPage } from '@/features/orders/OrdersListPage';
import { OrderDetailPage } from '@/features/orders/OrderDetailPage';
import { WAREHOUSE_STATUS_TABS } from '@/features/orders/labels';
import { CotizacionesListPage } from '@/features/quotes/CotizacionesListPage';
import { CotizacionCreatePage } from '@/features/quotes/CotizacionCreatePage';
import { CotizacionDetailPage } from '@/features/quotes/CotizacionDetailPage';
import { PaymentsListPage } from '@/features/payments/PaymentsListPage';
import { PaymentUploadPage } from '@/features/payments/PaymentUploadPage';
import { PaymentDetailPage } from '@/features/payments/PaymentDetailPage';
import { FacturasListPage } from '@/features/invoices/FacturasListPage';
import { FacturaDetailPage } from '@/features/invoices/FacturaDetailPage';
import { NotificationsPage } from '@/features/notifications/NotificationsPage';
import { ReportesPage } from '@/features/reports/ReportesPage';
import { ConfiguracionPage } from '@/features/settings/ConfiguracionPage';
import { DocumentosPage } from '@/features/documents/DocumentosPage';
import { CustomerOrderCreatePage } from '@/features/orders/CustomerOrderCreatePage';
import { RevisionDocumentosPage } from '@/features/documents/RevisionDocumentosPage';
import { PromocionesListPage } from '@/features/promotions/PromocionesListPage';
import { MarcasCategoriasPage } from '@/features/products/MarcasCategoriasPage';
import { PromocionCreatePage } from '@/features/promotions/PromocionCreatePage';
import { PromocionEditPage } from '@/features/promotions/PromocionEditPage';
import {
  CustomerHome,
  DistributorHome,
  SalesHome,
  WarehouseHome,
} from '@/features/portals/PortalHome';

/**
 * Route groups mirror docs/05: /public, /login, /request-account, and the
 * authenticated /app/{admin,sales,customer,distributor,warehouse}. Each app
 * group is wrapped in <RoleGuard> (UX-only) + <AppShell>. Built screens link to
 * real pages; remaining nav destinations render a Spanish "próximamente"
 * placeholder so the IA is complete without dead links.
 */
// Explicit annotation (via ReturnType) keeps the emitted type portable under `composite`
// — otherwise TS2742 wants to name react-router's internal @remix-run/router type.
export const router: ReturnType<typeof createBrowserRouter> = createBrowserRouter([
  { path: '/', element: <LandingPage /> },
  { path: '/public', element: <Navigate to="/" replace /> },
  { path: '/login', element: <LoginPage /> },
  { path: '/request-account', element: <RequestAccountPage /> },

  // Convenience: /app resolves to the signed-in user's home (or /login).
  { path: '/app', element: <RootRedirect /> },

  // ---- Admin (also finance lands here per routeGroupForRole) ----
  {
    path: '/app/admin',
    element: (
      <RoleGuard allow={ROLES_BY_GROUP.admin}>
        <AppShell />
      </RoleGuard>
    ),
    children: [
      { index: true, element: <AdminDashboard /> },
      { path: 'clientes', element: <CustomersListPage basePath="/app/admin/clientes" /> },
      { path: 'clientes/nuevo', element: <CustomerCreatePage /> },
      { path: 'clientes/:id', element: <CustomerDetailPage /> },
      { path: 'productos', element: <ProductsListPage basePath="/app/admin/productos" /> },
      { path: 'productos/nuevo', element: <ProductCreatePage /> },
      { path: 'productos/:id', element: <ProductDetailPage /> },
      { path: 'distribuidores', element: <DistributorsListPage /> },
      { path: 'distribuidores/nuevo', element: <DistributorCreatePage /> },
      { path: 'distribuidores/:id', element: <DistributorDetailPage /> },
      { path: 'vendedores', element: <Placeholder title="Vendedores" /> },
      { path: 'inventario', element: <InventoryListPage /> },
      {
        path: 'pedidos',
        element: (
          <OrdersListPage
            basePath="/app/admin/pedidos"
            description="Todos los pedidos. Filtra por estado."
            createPath="/app/admin/pedidos/nuevo"
            requireStatusFilter
          />
        ),
      },
      {
        path: 'pedidos/nuevo',
        element: <OrderCreatePage basePath="/app/admin/pedidos" ordersPath="/app/admin/pedidos" />,
      },
      { path: 'pedidos/:id', element: <OrderDetailPage basePath="/app/admin/pedidos" /> },
      {
        path: 'cotizaciones',
        element: (
          <CotizacionesListPage
            basePath="/app/admin/cotizaciones"
            description="Todas las cotizaciones. Filtra por estado."
            createPath="/app/admin/cotizaciones/nueva"
            requireStatusFilter
          />
        ),
      },
      {
        path: 'cotizaciones/nueva',
        element: (
          <CotizacionCreatePage
            basePath="/app/admin/cotizaciones"
            quotesPath="/app/admin/cotizaciones"
          />
        ),
      },
      {
        path: 'cotizaciones/:id',
        element: (
          <CotizacionDetailPage
            basePath="/app/admin/cotizaciones"
            ordersBasePath="/app/admin/pedidos"
          />
        ),
      },
      {
        path: 'facturas',
        element: (
          <FacturasListPage
            basePath="/app/admin/facturas"
            description="Todas las facturas. Filtra por estado."
          />
        ),
      },
      {
        path: 'facturas/:id',
        element: (
          <FacturaDetailPage basePath="/app/admin/facturas" orderBasePath="/app/admin/pedidos" />
        ),
      },
      {
        path: 'pagos',
        element: (
          <PaymentsListPage
            basePath="/app/admin/pagos"
            description="Todos los pagos. Revisa los pendientes."
            showUpload
          />
        ),
      },
      { path: 'pagos/subir', element: <PaymentUploadPage basePath="/app/admin/pagos" /> },
      { path: 'pagos/:id', element: <PaymentDetailPage basePath="/app/admin/pagos" /> },
      { path: 'documentos', element: <RevisionDocumentosPage /> },
      { path: 'credito', element: <Placeholder title="Crédito" /> },
      { path: 'marcas-categorias', element: <MarcasCategoriasPage /> },
      { path: 'promociones', element: <PromocionesListPage basePath="/app/admin/promociones" /> },
      { path: 'promociones/nueva', element: <PromocionCreatePage basePath="/app/admin/promociones" /> },
      { path: 'promociones/:id', element: <PromocionEditPage basePath="/app/admin/promociones" /> },
      { path: 'reportes', element: <ReportesPage /> },
      { path: 'configuracion', element: <ConfiguracionPage /> },
      { path: 'notificaciones', element: <NotificationsPage /> },
    ],
  },

  // ---- Sales ----
  {
    path: '/app/sales',
    element: (
      <RoleGuard allow={ROLES_BY_GROUP.sales}>
        <AppShell />
      </RoleGuard>
    ),
    children: [
      { index: true, element: <SalesHome /> },
      { path: 'clientes', element: <CustomersListPage basePath="/app/sales/clientes" /> },
      { path: 'clientes/nuevo', element: <CustomerCreatePage basePath="/app/sales/clientes" /> },
      { path: 'clientes/:id', element: <CustomerDetailPage basePath="/app/sales/clientes" /> },
      {
        path: 'crear-pedido',
        element: <OrderCreatePage basePath="/app/sales/pedidos" ordersPath="/app/sales/pedidos" />,
      },
      {
        path: 'pedidos',
        element: (
          <OrdersListPage
            basePath="/app/sales/pedidos"
            description="Tus pedidos y los de tus clientes."
            createPath="/app/sales/crear-pedido"
          />
        ),
      },
      { path: 'pedidos/:id', element: <OrderDetailPage basePath="/app/sales/pedidos" /> },
      {
        path: 'cotizaciones',
        element: (
          <CotizacionesListPage
            basePath="/app/sales/cotizaciones"
            description="Tus cotizaciones y las de tus clientes."
            createPath="/app/sales/cotizaciones/nueva"
          />
        ),
      },
      {
        path: 'cotizaciones/nueva',
        element: (
          <CotizacionCreatePage
            basePath="/app/sales/cotizaciones"
            quotesPath="/app/sales/cotizaciones"
          />
        ),
      },
      {
        path: 'cotizaciones/:id',
        element: (
          <CotizacionDetailPage
            basePath="/app/sales/cotizaciones"
            ordersBasePath="/app/sales/pedidos"
          />
        ),
      },
      {
        path: 'facturas',
        element: (
          <FacturasListPage
            basePath="/app/sales/facturas"
            description="Facturas de tus clientes, con su saldo y vencimiento."
          />
        ),
      },
      {
        path: 'facturas/:id',
        element: (
          <FacturaDetailPage basePath="/app/sales/facturas" orderBasePath="/app/sales/pedidos" />
        ),
      },
      {
        path: 'pagos',
        element: (
          <PaymentsListPage
            basePath="/app/sales/pagos"
            description="Pagos de tus clientes y su estado."
            showUpload
          />
        ),
      },
      { path: 'pagos/subir', element: <PaymentUploadPage basePath="/app/sales/pagos" /> },
      { path: 'pagos/:id', element: <PaymentDetailPage basePath="/app/sales/pagos" /> },
      { path: 'visitas', element: <Placeholder title="Visitas" /> },
      { path: 'metas', element: <Placeholder title="Metas" /> },
      { path: 'notificaciones', element: <NotificationsPage /> },
    ],
  },

  // ---- Customer ----
  {
    path: '/app/customer',
    element: (
      <RoleGuard allow={ROLES_BY_GROUP.customer}>
        <AppShell />
      </RoleGuard>
    ),
    children: [
      { index: true, element: <CustomerHome /> },
      { path: 'catalogo', element: <ProductsListPage orderable /> },
      { path: 'crear-pedido', element: <CustomerOrderCreatePage /> },
      {
        path: 'pedidos',
        element: (
          <OrdersListPage
            basePath="/app/customer/pedidos"
            title="Mis pedidos"
            description="Tus pedidos y su estado."
            createPath="/app/customer/crear-pedido"
          />
        ),
      },
      { path: 'pedidos/:id', element: <OrderDetailPage basePath="/app/customer/pedidos" backLabel="Mis pedidos" /> },
      {
        path: 'facturas',
        element: (
          <FacturasListPage
            basePath="/app/customer/facturas"
            title="Mis facturas"
            description="Tus facturas, con su saldo y vencimiento."
          />
        ),
      },
      {
        path: 'facturas/:id',
        element: (
          <FacturaDetailPage
            basePath="/app/customer/facturas"
            backLabel="Mis facturas"
            orderBasePath="/app/customer/pedidos"
          />
        ),
      },
      {
        path: 'pagos',
        element: (
          <PaymentsListPage
            basePath="/app/customer/pagos"
            title="Mis pagos"
            description="Tus pagos y su estado de revisión."
            showUpload
          />
        ),
      },
      { path: 'pagos/subir', element: <PaymentUploadPage basePath="/app/customer/pagos" /> },
      { path: 'pagos/:id', element: <PaymentDetailPage basePath="/app/customer/pagos" backLabel="Mis pagos" /> },
      { path: 'promociones', element: <PromocionesListPage title="Promociones" /> },
      { path: 'documentos', element: <DocumentosPage /> },
      { path: 'soporte', element: <Placeholder title="Soporte" /> },
      { path: 'notificaciones', element: <NotificationsPage /> },
    ],
  },

  // ---- Distributor ----
  {
    path: '/app/distributor',
    element: (
      <RoleGuard allow={ROLES_BY_GROUP.distributor}>
        <AppShell />
      </RoleGuard>
    ),
    children: [
      { index: true, element: <DistributorHome /> },
      { path: 'catalogo', element: <ProductsListPage /> },
      {
        path: 'crear-pedido',
        element: <DistributorOrderCreatePage />,
      },
      {
        path: 'pedidos',
        element: (
          <OrdersListPage
            basePath="/app/distributor/pedidos"
            description="Los pedidos de tus subclientes."
            createPath="/app/distributor/crear-pedido"
            showCustomer
          />
        ),
      },
      { path: 'pedidos/:id', element: <OrderDetailPage basePath="/app/distributor/pedidos" /> },
      {
        path: 'subclientes',
        element: (
          <CustomersListPage
            basePath="/app/distributor/subclientes"
            title="Subclientes"
            description="Los clientes vinculados a tu cuenta, con su saldo y crédito."
            emptyTitle="No hay subclientes"
            emptyDescription="Aún no tienes clientes vinculados a tu cuenta. Un administrador puede vincularlos."
          />
        ),
      },
      { path: 'subclientes/:id', element: <SubaccountDetailPage /> },
      {
        path: 'facturas',
        element: (
          <FacturasListPage
            basePath="/app/distributor/facturas"
            description="Tus facturas mayoristas, con su saldo y vencimiento."
          />
        ),
      },
      {
        path: 'facturas/:id',
        element: (
          <FacturaDetailPage
            basePath="/app/distributor/facturas"
            orderBasePath="/app/distributor/pedidos"
          />
        ),
      },
      {
        path: 'pagos',
        element: (
          <PaymentsListPage
            basePath="/app/distributor/pagos"
            description="Tus pagos mayoristas y su estado."
            showSubaccount
            showUpload
          />
        ),
      },
      { path: 'pagos/subir', element: <PaymentUploadPage basePath="/app/distributor/pagos" /> },
      { path: 'pagos/:id', element: <PaymentDetailPage basePath="/app/distributor/pagos" /> },
      { path: 'cuenta', element: <DistributorAccountPage /> },
      { path: 'notificaciones', element: <NotificationsPage /> },
    ],
  },

  // ---- Warehouse ----
  {
    path: '/app/warehouse',
    element: (
      <RoleGuard allow={ROLES_BY_GROUP.warehouse}>
        <AppShell />
      </RoleGuard>
    ),
    children: [
      { index: true, element: <WarehouseHome /> },
      {
        path: 'pedidos',
        element: (
          <OrdersListPage
            basePath="/app/warehouse/pedidos"
            title="Pedidos"
            description="Pedidos por preparar, despachar y entregar."
            statusTabs={WAREHOUSE_STATUS_TABS}
            requireStatusFilter
          />
        ),
      },
      { path: 'pedidos/:id', element: <OrderDetailPage basePath="/app/warehouse/pedidos" /> },
      {
        path: 'despachos',
        element: (
          <OrdersListPage
            basePath="/app/warehouse/pedidos"
            title="Despachos"
            description="Pedidos despachados, pendientes de entrega."
            statusTabs={WAREHOUSE_STATUS_TABS}
            requireStatusFilter
            defaultStatus={OrderStatus.DISPATCHED}
          />
        ),
      },
      { path: 'inventario', element: <InventoryListPage /> },
      { path: 'notificaciones', element: <NotificationsPage /> },
    ],
  },

  { path: '*', element: <NotFoundPage /> },
]);

// The saved theme belongs to the signed-in app. index.html sets it before the first paint for any
// /app URL, and AppShell only mounts for a signed-in user, so a visitor sent from /app to the login
// (or any public page) would keep the dark tokens: drop them as soon as the route leaves /app.
router.subscribe((state) => {
  if (!isAppPath(state.location.pathname)) document.documentElement.removeAttribute('data-theme');
});
